import React, { useState, useEffect } from "react";
import {
  Sparkles,
  Play,
  Clock,
  Coffee,
  TrendingUp,
  CheckCircle2,
  FileText,
  Users,
  LogOut,
  CheckCircle,
} from "lucide-react";
import confetti from "canvas-confetti";
import { AnimatePresence, motion } from "framer-motion";

const FEATURES = [
  {
    title: "One-Click Check-in",
    desc: "Start your day instantly",
    Icon: Clock,
    tone: "cyan",
  },
  {
    title: "Break Management",
    desc: "Track breaks automatically",
    Icon: Coffee,
    tone: "orange",
  },
  {
    title: "Real-time Tracking",
    desc: "Live work hour counter",
    Icon: TrendingUp,
    tone: "emerald",
  },
  {
    title: "Simple Check-out",
    desc: "End your day with one click",
    Icon: CheckCircle2,
    tone: "purple",
  },
  {
    title: "Automated Logs",
    desc: "No manual timesheet entry",
    Icon: FileText,
    tone: "blue",
  },
  {
    title: "Team Visibility",
    desc: "Managers see live status",
    Icon: Users,
    tone: "pink",
  },
];

const STATS = [
  { label: "Checked In", value: "142", Icon: CheckCircle2, tone: "cyan" },
  { label: "On Break", value: "18", Icon: Coffee, tone: "orange" },
  { label: "Avg. Work Time", value: "00:00:00", Icon: Clock, tone: "cyan" },
  { label: "Checked Out", value: "67", Icon: LogOut, tone: "blue" },
];

const FEED = [
  {
    name: "Sarah Johnson",
    action: "Checked In",
    time: "2 mins ago",
    dot: "green pulse",
  },
  {
    name: "Mike Chen",
    action: "On Break",
    time: "5 mins ago",
    dot: "orange pulse",
  },
  {
    name: "Emily Davis",
    action: "Checked Out",
    time: "8 mins ago",
    dot: "cyan",
  },
  {
    name: "John Smith",
    action: "Resumed Work",
    time: "12 mins ago",
    dot: "green pulse",
  },
];

export default function OneClickAttendance() {
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [checkInState, setCheckInState] = useState("start");
  const [effectType, setEffectType] = useState("success");
  const [showCinematicEffect, setShowCinematicEffect] = useState(false);
  const [showCheckInModal, setShowCheckInModal] = useState(false);
  const [isOnBreak, setIsOnBreak] = useState(false);

  // ✅ Timer effect: runs only when working AND not on break
  useEffect(() => {
    let interval;
    if (checkInState === "working" && !isOnBreak) {
      interval = setInterval(() => {
        setElapsedSeconds((prev) => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [checkInState, isOnBreak]);

  const handleCheckIn = () => {
    setEffectType("checkin");
    setShowCinematicEffect(true);
    setTimeout(() => setShowCinematicEffect(false), 2000);

    setCheckInState("working");
    setElapsedSeconds(0);
    setShowCheckInModal(true);
    setIsOnBreak(false); // ensure break state is fresh

    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
      colors: ["#093a5b", "#1d6fa4", "#0ea5e9"],
    });
  };

  const handleCheckOut = () => {
    setEffectType("checkout");
    setShowCinematicEffect(true);
    setTimeout(() => setShowCinematicEffect(false), 2000);

    setCheckInState("done");
    setShowCheckInModal(false);
    setIsOnBreak(false); // reset break on checkout

    confetti({
      particleCount: 120,
      spread: 90,
      origin: { y: 0.6 },
      colors: ["#093a5b", "#1d6fa4", "#0ea5e9"],
    });
  };

  const getCheckInStateStyles = () => {
    switch (checkInState) {
      case "start":
        return {
          bg: "from-[#093a5b] to-[#1d6fa4]",
          text: "Start Your Day",
          icon: <Play className="w-5 h-5" />,
        };
      case "working":
        return {
          bg: "from-[#1d6fa4] to-[#093a5b]",
          text: "Currently Working",
          icon: <Clock className="w-5 h-5" />,
        };
      case "done":
        return {
          bg: "from-[#093a5b] to-[#1d6fa4]",
          text: "All Done!",
          icon: <CheckCircle className="w-5 h-5" />,
        };
    }
  };

  const formatElapsedTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <section className="oca-wrap">
      <div className="oca-inner">
        {/* <div className="oca-bg-grad" /> */}
        {/* <div className="oca-bg-orb" /> */}

        <div className="oca-container">
          {/* header */}
          <div className="oca-head">
            {/* <div className="oca-pill">
              <Sparkles className="oca-pill-icon" />
              <span className="oca-pill-text">No Fingerprint Required</span>
            </div> */}
            <h3 className="oca-title">One-Click Attendance</h3>
            <p className="oca-subtitle">
              No fingerprint, no scanning. Just click and go.
            </p>
          </div>

          {/* CTA */}
          <div className="oca-cta-wrap">
            {/* <div className="oca-cta-glow" /> */}
            <span className="oca-dot d1" />
            <span className="oca-dot d2" />
            <span className="oca-dot d3" />
            <span className="oca-dot d4" />
            <span className="oca-dot d5" />
            <span className="oca-dot d6" />

            <div className="oca-cta">
              <span className="oca-ring r1" />
              <span className="oca-ring r2" />

              <motion.button
                onClick={() => {
                  if (checkInState === "start") {
                    handleCheckIn();
                  } else if (checkInState === "working") {
                    setShowCheckInModal(true);
                  } else {
                    setCheckInState("start");
                    setElapsedSeconds(0);
                    setIsOnBreak(false);
                  }
                }}
                whileHover={{
                  scale: 1.05,
                  boxShadow: "0 20px 60px rgba(6, 182, 212, 0.3)",
                }}
                whileTap={{ scale: 0.98 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
                className={`relative px-10 py-5 rounded-full bg-gradient-to-r ${getCheckInStateStyles().bg} text-white shadow-2xl overflow-hidden`}
              >
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent"
                  animate={{ x: ["-200%", "200%"] }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    repeatDelay: 2,
                    ease: "easeInOut",
                  }}
                />
                <div className="relative flex items-center gap-3">
                  <motion.div
                    animate={
                      checkInState === "working" ? { scale: [1, 1.2, 1] } : {}
                    }
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  >
                    {getCheckInStateStyles().icon}
                  </motion.div>
                  <span className="text-lg font-medium">
                    {getCheckInStateStyles().text}
                  </span>
                  {checkInState === "working" && (
                    <motion.span
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="ml-2 px-3 py-1 bg-white/20 rounded-full text-sm font-mono"
                    >
                      {formatElapsedTime(elapsedSeconds)}
                    </motion.span>
                  )}
                </div>
              </motion.button>
            </div>
          </div>

          {/* Working Modal */}
          <AnimatePresence>
            {showCheckInModal && checkInState === "working" && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="fixed inset-0 bg-black/60 backdrop-blur-md z-50 flex items-center justify-center p-6"
                onClick={() => setShowCheckInModal(false)}
              >
                <motion.div
                  initial={{ opacity: 0, y: 40, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 40, scale: 0.95 }}
                  transition={{ duration: 0.4, ease: "easeOut" }}
                  onClick={(e) => e.stopPropagation()}
                  className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl border border-gray-200"
                >
                  <div className="text-center mb-8">
                    <motion.div
                      animate={{ scale: [1, 1.05, 1] }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: "easeInOut",
                      }}
                      className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-4" style={{background: 'rgba(9,58,91,0.1)'}}
                    >
                      <Clock className="w-10 h-10" style={{color: '#093a5b'}} />
                    </motion.div>
                    <h3 className="text-3xl font-bold mb-2" style={{color: '#093a5b'}}>
                      Currently Working
                    </h3>
                    <p className="text-gray-500">You checked in at 09:00 AM</p>
                  </div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="rounded-2xl p-6 mb-6 border border-gray-100" style={{background: 'rgba(9,58,91,0.04)'}}
                  >
                    <div className="text-center">
                      <p className="text-gray-500 text-sm mb-2">Elapsed Time</p>
                      <motion.p
                        animate={{ opacity: [1, 0.7, 1] }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          ease: "easeInOut",
                        }}
                        className="text-5xl font-mono font-bold" style={{color: '#093a5b'}}
                      >
                        {formatElapsedTime(elapsedSeconds)}
                      </motion.p>
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="space-y-3"
                  >
                    <motion.button
                      onClick={() => setIsOnBreak(!isOnBreak)}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      transition={{ duration: 0.2 }}
                      className={`w-full px-6 py-4 rounded-xl font-medium transition-all ${
                        isOnBreak
                          ? "bg-orange-500 hover:bg-orange-600 text-white"
                          : "bg-gray-100 hover:bg-gray-200 text-gray-700"
                      }`}
                    >
                      <div className="flex items-center justify-center gap-2">
                        {isOnBreak ? (
                          <Play className="w-5 h-5" />
                        ) : (
                          <Coffee className="w-5 h-5" />
                        )}
                        {isOnBreak ? "Resume Work" : "Take a Break"}
                      </div>
                    </motion.button>

                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      transition={{ duration: 0.2 }}
                      onClick={handleCheckOut}
                      className="w-full px-6 py-4 text-white rounded-xl font-medium transition-all" style={{background: '#093a5b'}}
                    >
                      <div className="flex items-center justify-center gap-2">
                        <LogOut className="w-5 h-5" />
                        Check Out
                      </div>
                    </motion.button>
                  </motion.div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Feature Cards */}
          <div className="oca-cards">
            {FEATURES.map(({ title, desc, Icon, tone }) => (
              <div className="oca-card" key={title}>
                <div className={`oca-card-iconbox tone-${tone}`}>
                  <Icon className={`oca-card-icon tone-${tone}-text`} />
                </div>
                <h4 className="oca-card-title">{title}</h4>
                <p className="oca-card-desc">{desc}</p>
              </div>
            ))}
          </div>

          {/* Dashboard */}
          <div className="oca-dash">
            <div className="oca-dash-head">
              <h3 className="oca-dash-title">📊 Live Tracking Dashboard</h3>
              <p className="oca-dash-sub">
                Real-time automated attendance tracking for today
              </p>
            </div>

            <div className="oca-stats">
              {STATS.map(({ label, value, Icon, tone }) => (
                <div className="oca-stat" key={label}>
                  <div className={`oca-stat-iconbox tone-${tone}`}>
                    <Icon className={`oca-stat-icon tone-${tone}-text`} />
                  </div>
                  <div className="oca-stat-value">{value}</div>
                  <div className="oca-stat-label">{label}</div>
                </div>
              ))}
            </div>

            {/* Feed */}
            <div className="oca-feed">
              <div className="oca-feed-title">
                <Sparkles className="oca-feed-title-icon" />
                Recent Activity (Live Feed)
              </div>
              <div className="oca-feed-list">
                {FEED.map((x, i) => (
                  <div className="oca-feed-item" key={i}>
                    <div className="oca-feed-left">
                      <span className={`oca-feed-dot ${x.dot}`} />
                      <div>
                        <div className="oca-feed-name">{x.name}</div>
                        <div className="oca-feed-action">{x.action}</div>
                      </div>
                    </div>
                    <div className="oca-feed-time">{x.time}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
